/**
 * @fileoverview Job Board - Drag-and-drop interface for managing technician assignments.
 *
 * Features:
 * - Unassigned jobs column (admin only)
 * - Technician columns with assigned jobs
 * - Drag-and-drop job assignment
 * - Date navigation and calendar picker
 * - Vehicle Health Check (VHC) integration
 * - Search/filter functionality
 * - Rollover jobs to tomorrow
 *
 * Role-based views:
 * - Admin: Sees all columns, can assign/reassign jobs, sees costs
 * - Technician: Sees only their column, no costs shown
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  Tooltip,
  Badge,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Popover,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  DirectionsCar as CarIcon,
  Schedule as ScheduleIcon,
  CreditCard as CardIcon,
  Notes as NotesIcon,
  Engineering as EngineeringIcon,
  Refresh as RefreshIcon,
  Build as BuildIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CalendarMonth as CalendarIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  FastForward as FastForwardIcon,
  Edit as EditIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { format, addDays, subDays, startOfWeek } from 'date-fns';
import { enUS } from 'date-fns/locale';
import api from '../../services/api';
import VehicleHealthCheck from '../../components/VehicleHealthCheck';
import EditBookingModal from '../../components/EditBookingModal';
import { useSelector } from 'react-redux';

/**
 * JobCard - Draggable card representing a single booking/job.
 *
 * @param {Object} props
 * @param {Object} props.booking - Booking data with customer, vehicle, status
 * @param {Function} props.onDragStart - Handler when drag begins
 * @param {Function} props.onDragEnd - Handler when drag ends
 * @param {boolean} props.isDragging - Whether card is being dragged
 * @param {Function} props.onCardClick - Handler for card click (opens detail modal)
 * @param {Function} props.onStartHealthCheck - Handler to start VHC
 * @param {Function} props.onEdit - Handler to edit booking (admin only)
 * @param {boolean} props.showHealthCheck - Show VHC button (default: false)
 * @param {boolean} props.isTechnician - Hide costs if true (default: false)
 */
const JobCard = ({ booking, onDragStart, onDragEnd, isDragging, onCardClick, onStartHealthCheck, onEdit, showHealthCheck = false, isTechnician = false }) => {
  const theme = useTheme();
  const [dragStarted, setDragStarted] = useState(false);

  const handleDragStart = (e) => {
    setDragStarted(true);
    onDragStart(e, booking);
  };

  const handleDragEnd = (e) => {
    setDragStarted(false);
    onDragEnd(e);
  };

  const handleCardClick = (e) => {
    // Prevent click when dragging or right after drag
    if (isDragging || dragStarted) {
      setTimeout(() => setDragStarted(false), 100);
      return;
    }
    if (onCardClick) {
      onCardClick(booking);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: '#2196f3',
      assigned: '#ff9800',
      in_progress: '#4caf50',
      waiting_parts: '#f44336',
      pending_admin_review: '#9c27b0',
      completed: '#9e9e9e',
    };
    return colors[status] || '#2196f3';
  };

  return (
    <Card
      draggable={true}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleCardClick}
      sx={{
        mb: 1,
        cursor: 'grab',
        opacity: isDragging ? 0.5 : 1,
        transition: 'all 0.2s ease',
        userSelect: 'none',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
          cursor: onCardClick ? 'pointer' : 'grab',
        },
        '&:active': {
          cursor: 'grabbing',
        },
        '&:drag': {
          cursor: 'grabbing',
        },
        borderLeft: `4px solid ${getStatusColor(booking.status)}`,
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="subtitle2" fontWeight="bold">
            {booking.customer_name}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            {!isTechnician && onEdit && (
              <Tooltip title="Edit Booking">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(booking);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            <Chip
              label={booking.status.replace('_', ' ')}
              size="small"
              sx={{
                backgroundColor: getStatusColor(booking.status),
                color: 'white',
                fontSize: '0.7rem',
              }}
            />
          </Box>
        </Box>

        <Box display="flex" alignItems="center" mb={0.5}>
          <CarIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {booking.vehicle_info}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" mb={0.5}>
          <ScheduleIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {new Date(booking.booking_date).toLocaleDateString()}
            {booking.time_of_day && ` (${booking.time_of_day})`}
          </Typography>
        </Box>

        {booking.customer_phone && (
          <Box display="flex" alignItems="center" mb={0.5}>
            <PhoneIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {booking.customer_phone}
            </Typography>
          </Box>
        )}

        {booking.location && (
          <Box display="flex" alignItems="center" mb={0.5}>
            <LocationIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {booking.location}
            </Typography>
          </Box>
        )}

        {/* Hide pricing for technicians */}
        {!isTechnician && booking.total_cost > 0 && (
          <Box display="flex" alignItems="center" mb={0.5}>
            <CardIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              £{booking.total_cost.toFixed(2)}
            </Typography>
          </Box>
        )}

        {booking.work_required && booking.work_required.length > 0 && (
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary" display="block">
              Work Required:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
              {booking.work_required.slice(0, 2).map((work, index) => (
                <Chip
                  key={index}
                  label={typeof work === 'string' ? work : work.service || 'Service'}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.6rem', height: 20 }}
                />
              ))}
              {booking.work_required.length > 2 && (
                <Chip
                  label={`+${booking.work_required.length - 2}`}
                  size="small"
                  sx={{ fontSize: '0.6rem', height: 20 }}
                />
              )}
            </Box>
          </Box>
        )}

        {booking.notes && (
          <Tooltip title={booking.notes}>
            <Box display="flex" alignItems="center" mt={1}>
              <NotesIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                {booking.notes.substring(0, 30)}...
              </Typography>
            </Box>
          </Tooltip>
        )}

        {booking.estimated_completion_time && (
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Est. Time: {booking.estimated_completion_time}
          </Typography>
        )}

        {/* Job Request Details */}
        {booking.job_request && (
          <Box mt={1} p={1} sx={{
            backgroundColor: 'success.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'success.200'
          }}>
            <Typography variant="caption" color="success.dark" sx={{ fontWeight: 'bold' }} display="block">
              Parts Request Completed:
            </Typography>

            {booking.job_request.items && booking.job_request.items.length > 0 && (
              <Box mt={0.5}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Items ({booking.job_request.items.length}):
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                  {booking.job_request.items.slice(0, 2).map((item, index) => (
                    <Chip
                      key={index}
                      label={isTechnician
                        ? `${item.quantity}x ${item.name}`
                        : `${item.quantity}x ${item.name} - £${(item.price * item.quantity).toFixed(2)}`
                      }
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.6rem', height: 18 }}
                    />
                  ))}
                  {booking.job_request.items.length > 2 && (
                    <Chip
                      label={`+${booking.job_request.items.length - 2} more`}
                      size="small"
                      sx={{ fontSize: '0.6rem', height: 18 }}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* Hide additional cost for technicians */}
            {!isTechnician && booking.job_request.total_additional_cost > 0 && (
              <Typography variant="caption" color="success.dark" display="block" mt={0.5} sx={{ fontWeight: 'bold' }}>
                Additional Cost: £{booking.job_request.total_additional_cost.toFixed(2)}
              </Typography>
            )}

            {booking.job_request.admin_notes && (
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5} sx={{ fontStyle: 'italic' }}>
                Admin Notes: {booking.job_request.admin_notes.length > 40
                  ? `${booking.job_request.admin_notes.substring(0, 40)}...`
                  : booking.job_request.admin_notes}
              </Typography>
            )}
          </Box>
        )}

        {showHealthCheck && onStartHealthCheck && (
          <Box mt={2} pt={1} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            <Grid container spacing={1}>
              {showHealthCheck && (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    startIcon={<AssignmentIcon />}
                    disabled={booking.has_health_check || false}
                    onClick={(e) => {
                      e.stopPropagation();
                      onStartHealthCheck(booking);
                    }}
                    sx={{
                      fontSize: '0.7rem',
                      py: 0.5,
                      borderColor: 'success.light',
                      color: 'success.main',
                      '&:hover': {
                        borderColor: 'success.main',
                        backgroundColor: 'success.50',
                      },
                    }}
                  >
                    Health Check
                  </Button>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const TechnicianColumn = ({ technician, onDrop, onDragOver, onCardClick, onDragStart, onDragEnd, draggedBooking, onStartHealthCheck, onEdit, isTechnician = false }) => {
  const theme = useTheme();

  const getStatusColor = (status) => {
    const colors = {
      available: '#4caf50',
      busy: '#ff9800',
      unavailable: '#f44336',
      break: '#2196f3',
      sick: '#f44336',
      vacation: '#9c27b0',
    };
    return colors[status] || '#9e9e9e';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    onDrop(e, technician.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    onDragOver(e);
  };

  return (
    <Paper
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      sx={{
        height: '100%',
        p: 2,
        backgroundColor: theme.palette.grey[50],
        border: '2px dashed transparent',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: theme.palette.primary.light,
        },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center">
          <EngineeringIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {technician.full_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {technician.employee_id}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center">
          <Badge
            badgeContent={technician.current_capacity}
            max={technician.max_concurrent_jobs}
            color={technician.current_capacity >= technician.max_concurrent_jobs ? 'error' : 'primary'}
            sx={{ mr: 1 }}
          >
            <AssignmentIcon />
          </Badge>
          <Chip
            label={technician.status}
            size="small"
            sx={{
              backgroundColor: getStatusColor(technician.status),
              color: 'white',
              fontSize: '0.7rem',
            }}
          />
        </Box>
      </Box>

      {technician.current_location && (
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          📍 {technician.current_location}
        </Typography>
      )}

      {technician.specialization_display && technician.specialization_display.length > 0 && (
        <Box mb={2}>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            Specializations:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {technician.specialization_display.slice(0, 3).map((spec, index) => (
              <Chip
                key={index}
                label={spec}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.6rem', height: 18 }}
              />
            ))}
            {technician.specialization_display.length > 3 && (
              <Chip
                label={`+${technician.specialization_display.length - 3}`}
                size="small"
                sx={{ fontSize: '0.6rem', height: 18 }}
              />
            )}
          </Box>
        </Box>
      )}

      <Typography variant="subtitle2" gutterBottom>
        Assigned Jobs ({technician.current_capacity}/{technician.max_concurrent_jobs})
      </Typography>

      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        pr: 1,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a1a1a1',
        },
      }}>
        {technician.assigned_bookings
          .filter(booking => {
            // Filter out bookings awaiting customer payment
            return !(booking.status === 'pending_admin_review' &&
                    booking.payment_required === true &&
                    booking.payment_status === 'pending');
          })
          .map((booking) => (
          <JobCard
            key={booking.id}
            booking={booking}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            isDragging={draggedBooking?.id === booking.id}
            onCardClick={onCardClick}
            onStartHealthCheck={onStartHealthCheck}
            onEdit={onEdit}
            showHealthCheck={booking.status === 'assigned' || booking.status === 'in_progress'}
            isTechnician={isTechnician}
          />
        ))}
        {technician.assigned_bookings
          .filter(booking => {
            // Filter out bookings awaiting customer payment
            return !(booking.status === 'pending_admin_review' &&
                    booking.payment_required === true &&
                    booking.payment_status === 'pending');
          }).length === 0 && (
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'grey.300',
              borderRadius: 1,
              p: 2,
              textAlign: 'center',
              color: 'text.secondary',
              minHeight: '100px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2">
              Drop jobs here to assign to {technician.full_name}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

const JobBoard = () => {
  const user = useSelector((state) => state.auth.user);
  const isTechnician = user?.role === 'technician';
  const isAdmin = ['admin', 'org_admin', 'garage_manager'].includes(user?.role) || user?.is_superuser;
  const [data, setData] = useState({ unassigned_bookings: [], technicians: [] });
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rolloverDialogOpen, setRolloverDialogOpen] = useState(false);
  const [rolloverLoading, setRolloverLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [draggedBooking, setDraggedBooking] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [isDragOverUnassigned, setIsDragOverUnassigned] = useState(false);
  // Parts Request flow removed
  const [healthCheckModalOpen, setHealthCheckModalOpen] = useState(false);
  const [healthCheckBooking, setHealthCheckBooking] = useState(null);
  const [viewHealthCheckId, setViewHealthCheckId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [datesWithJobs, setDatesWithJobs] = useState([]);
  const [unassignedSearch, setUnassignedSearch] = useState('');

  useEffect(() => {
    fetchJobBoardData(!initialLoad);
    if (initialLoad) {
      fetchDatesWithJobs();
    }
  }, [selectedDate]);

  const fetchJobBoardData = async (showLoader = true) => {
    if (showLoader) {
      setLoading(true);
    }
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await api.get(`/v1/job-board/?date=${dateStr}`);
      setData(response.data);
    } catch (err) {
      setError('Failed to load job board: ' + (err.response?.data?.error || err.message));
    } finally {
      if (showLoader) {
        setLoading(false);
      }
      if (initialLoad) {
        setInitialLoad(false);
        setLoading(false);
      }
    }
  };

  const fetchDatesWithJobs = async () => {
    try {
      const response = await api.get('/v1/job-board/dates/');
      console.log('📅 Dates with jobs:', response.data.dates);
      setDatesWithJobs(response.data.dates);
    } catch (err) {
      console.warn('Failed to load job dates for calendar:', err);
    }
  };

  const handleRolloverToTomorrow = async () => {
    setRolloverLoading(true);
    try {
      const response = await api.post('/v1/job-board/rollover-to-tomorrow/');
      setSuccess(response.data.message);
      setRolloverDialogOpen(false);
      // Refresh data after rollover
      fetchJobBoardData(false);
      fetchDatesWithJobs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to rollover jobs');
    } finally {
      setRolloverLoading(false);
    }
  };

  const fetchJobsForDate = async (dateStr) => {
    console.log('📅 fetchJobsForDate called with:', dateStr);
    try {
      setLoading(true);
      console.log('📅 Making API call to:', `/v1/job-board/?date=${dateStr}`);
      const response = await api.get(`/v1/job-board/?date=${dateStr}`);
      console.log('📅 API response:', response.data);
      setData(response.data);
      console.log(`📅 Fetched jobs for ${dateStr}:`, response.data);
    } catch (err) {
      console.error('📅 Error fetching jobs for date:', err);
      setError('Failed to load jobs for selected date: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, booking) => {
    setDraggedBooking(booking);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedBooking(null);
  };

  const handleJobCardClick = async (booking) => {
    setSelectedBooking(booking);
    setDetailModalOpen(true);

    // Fetch health checks for this booking
    try {
      const response = await api.get(`/v1/bookings/${booking.id}/health-checks/`);
      setSelectedBooking(prev => ({
        ...prev,
        healthChecks: response.data
      }));
    } catch (error) {
      console.error('Error fetching health checks:', error);
    }
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedBooking(null);
  };

  const handleEditBooking = (booking) => {
    setEditingBooking(booking);
    setEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setEditingBooking(null);
  };

  const handleEditSave = (updatedBooking) => {
    // Refresh the job board data after edit
    fetchJobBoardData(false);
    fetchDatesWithJobs();
  };

  // Parts Request flow removed

  const handleStartHealthCheck = (booking) => {
    setHealthCheckBooking(booking);
    setHealthCheckModalOpen(true);
  };

  const handleCloseHealthCheckModal = () => {
    setHealthCheckModalOpen(false);
    setHealthCheckBooking(null);
    setViewHealthCheckId(null);
  };

  const handleHealthCheckComplete = (result) => {
    fetchJobBoardData(false); // Refresh the data without loader
    if (result.success === false) {
      setError(result.message || 'Error with health check');
    } else {
      setSuccess(result.message || 'Health check completed successfully');
    }
  };

  const handleDrop = async (e, technicianId) => {
    e.preventDefault();
    if (!draggedBooking) return;

    // Note: We don't check if already assigned to this technician since the API will handle it
    // and the backend will return appropriate response

    try {
      setError('');
      setSuccess('');

      const response = await api.post(`/v1/bookings/${draggedBooking.id}/assign/`, {
        technician_id: technicianId,
      });

      setSuccess(response.data.message);
      // Small delay to ensure backend is updated before refetch
      setTimeout(() => {
        fetchJobBoardData(false);
      }, 100);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }

    setDraggedBooking(null);
  };

  const handleUnassignedDrop = async (e) => {
    e.preventDefault();
    console.log('💧 Drop on unassigned column!', draggedBooking?.customer_name);
    console.log('📊 Booking data:', {
      assigned_technician: draggedBooking?.assigned_technician,
      technician_id: draggedBooking?.technician_id,
      status: draggedBooking?.status
    });
    setIsDragOverUnassigned(false);
    if (!draggedBooking) {
      console.log('❌ No dragged booking found');
      return;
    }

    // Check if booking is already unassigned (status should be 'assigned' if it's assigned to a technician)
    if (draggedBooking.status !== 'assigned') {
      console.log('⚠️ Booking is not in assigned status - refreshing data anyway');
      setTimeout(() => {
        fetchJobBoardData(false);
      }, 100);
      setDraggedBooking(null);
      return; // Not assigned, just refresh data
    }

    try {
      setError('');
      setSuccess('');

      const response = await api.post(`/v1/bookings/${draggedBooking.id}/assign/`, {
        technician_id: null,
      });

      setSuccess(response.data.message || 'Booking unassigned');
      // Small delay to ensure backend is updated before refetch
      setTimeout(() => {
        fetchJobBoardData(false);
      }, 100);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }

    setDraggedBooking(null);
  };

  const handleUnassignedDragOver = (e) => {
    e.preventDefault();
    console.log('📍 Drag over unassigned column');
    setIsDragOverUnassigned(true);
  };

  const handleUnassignedDragLeave = (e) => {
    e.preventDefault();
    console.log('🚪 Drag leave unassigned column');
    setIsDragOverUnassigned(false);
  };

  // Filter unassigned bookings based on search
  const filteredUnassignedBookings = data.unassigned_bookings.filter(booking => {
    if (!unassignedSearch.trim()) return true;

    const searchLower = unassignedSearch.toLowerCase();
    return (
      booking.customer_name?.toLowerCase().includes(searchLower) ||
      booking.customer_phone?.toLowerCase().includes(searchLower) ||
      booking.vehicle_info?.toLowerCase().includes(searchLower) ||
      booking.id?.toString().includes(searchLower) ||
      booking.work_required?.some(work =>
        work.service?.toLowerCase().includes(searchLower)
      )
    );
  });

  if (loading && initialLoad) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" component="h1">
          <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Job Board
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            onClick={() => setSelectedDate(subDays(selectedDate, 1))}
            color="primary"
            size="small"
          >
            <ChevronLeftIcon />
          </IconButton>
          <Button
            onClick={(e) => setCalendarAnchor(e.currentTarget)}
            variant="outlined"
            size="small"
            startIcon={<CalendarIcon />}
            sx={{ minWidth: '150px' }}
          >
            {format(selectedDate, 'MMM dd, yyyy')}
          </Button>
          <IconButton
            onClick={() => setSelectedDate(addDays(selectedDate, 1))}
            color="primary"
            size="small"
          >
            <ChevronRightIcon />
          </IconButton>
          <IconButton onClick={() => fetchJobBoardData(false)} color="primary">
            <RefreshIcon />
          </IconButton>
          {isAdmin && (
            <Tooltip title="Move today's incomplete jobs to tomorrow">
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={<FastForwardIcon />}
                onClick={() => setRolloverDialogOpen(true)}
                sx={{ ml: 2 }}
              >
                Rollover to Tomorrow
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Rollover Confirmation Dialog */}
      <Dialog
        open={rolloverDialogOpen}
        onClose={() => setRolloverDialogOpen(false)}
      >
        <DialogTitle>Rollover Jobs to Tomorrow</DialogTitle>
        <DialogContent>
          <Typography>
            This will move all incomplete jobs from <strong>today</strong> to <strong>tomorrow</strong>.
          </Typography>
          <Typography sx={{ mt: 2, color: 'text.secondary' }}>
            • Assigned technicians will remain unchanged<br />
            • Only jobs with status: assigned, in progress, waiting parts, or awaiting payment will be moved
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRolloverDialogOpen(false)} disabled={rolloverLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleRolloverToTomorrow}
            variant="contained"
            color="warning"
            disabled={rolloverLoading}
            startIcon={rolloverLoading ? <CircularProgress size={20} /> : <FastForwardIcon />}
          >
            {rolloverLoading ? 'Moving...' : 'Rollover Jobs'}
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(calendarAnchor)}
        anchorEl={calendarAnchor}
        onClose={() => setCalendarAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
          <DateCalendar
            value={selectedDate}
            onChange={(newDate) => {
              console.log('📅 Calendar date selected:', newDate);
              setSelectedDate(newDate);
              setCalendarAnchor(null);
              // Fetch jobs for the selected date
              if (newDate) {
                const dateStr = format(newDate, 'yyyy-MM-dd');
                console.log('📅 Fetching jobs for date:', dateStr);
                fetchJobsForDate(dateStr);
              }
            }}
            showDaysOutsideCurrentMonth={true}
            fixedWeekNumber={6}
            slots={{
              day: (dayProps) => {
                const { day, ...other } = dayProps;
                const dateStr = format(day, 'yyyy-MM-dd');
                const hasJobs = datesWithJobs.includes(dateStr);

                return (
                  <PickersDay
                    {...other}
                    day={day}
                    sx={{
                      ...(hasJobs && {
                        backgroundColor: '#ff9800 !important',
                        color: '#ffffff !important',
                        fontWeight: 'bold',
                        '&:hover': {
                          backgroundColor: '#f57c00 !important',
                        },
                        '&.Mui-selected': {
                          backgroundColor: '#f57c00 !important',
                        },
                      }),
                    }}
                  />
                );
              },
            }}
          />
        </LocalizationProvider>
      </Popover>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          flex: 1,
          minHeight: 0,
          pb: 1,
          opacity: loading && !initialLoad ? 0.6 : 1,
          transition: 'opacity 0.2s ease',
        }}
      >
        {/* Unassigned Jobs Column - Only visible to admins */}
        {!isTechnician && (
          <Paper
            onDrop={handleUnassignedDrop}
            onDragOver={handleUnassignedDragOver}
            onDragLeave={handleUnassignedDragLeave}
            sx={{
              minWidth: '300px',
              maxWidth: '300px',
              p: 2,
              backgroundColor: isDragOverUnassigned ? 'warning.50' : 'grey.50',
              border: `2px dashed ${isDragOverUnassigned ? '#ff9800' : 'transparent'}`,
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'warning.light',
              },
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h6" gutterBottom display="flex" alignItems="center">
              <Badge badgeContent={data.unassigned_bookings.length} color="warning" sx={{ mr: 1 }}>
                <AssignmentIcon />
              </Badge>
              Unassigned Jobs
            </Typography>

            {/* Search Box */}
            <TextField
              size="small"
              placeholder="Search jobs..."
              value={unassignedSearch}
              onChange={(e) => setUnassignedSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2, width: '100%' }}
            />

            <Box
              onDrop={handleUnassignedDrop}
              onDragOver={handleUnassignedDragOver}
              onDragLeave={handleUnassignedDragLeave}
              sx={{
                flex: 1,
                overflowY: 'auto',
                pr: 1,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#c1c1c1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#a1a1a1',
                },
              }}>
              {filteredUnassignedBookings.map((booking) => (
                <JobCard
                  key={booking.id}
                  booking={booking}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  isDragging={draggedBooking?.id === booking.id}
                  onCardClick={handleJobCardClick}
                  onEdit={handleEditBooking}
                />
              ))}
              {filteredUnassignedBookings.length === 0 && (
                <Box
                  onDrop={handleUnassignedDrop}
                  onDragOver={handleUnassignedDragOver}
                  onDragLeave={handleUnassignedDragLeave}
                  sx={{
                    border: `2px dashed ${isDragOverUnassigned ? '#ff9800' : '#d0d0d0'}`,
                    borderColor: isDragOverUnassigned ? 'warning.main' : 'grey.300',
                    borderRadius: 1,
                    p: 2,
                    textAlign: 'center',
                    color: 'text.secondary',
                    minHeight: '100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isDragOverUnassigned ? 'warning.50' : 'transparent',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Typography variant="body2">
                    {isDragOverUnassigned
                      ? 'Drop here to unassign'
                      : unassignedSearch.trim()
                      ? 'No jobs match your search'
                      : 'No unassigned jobs'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        )}

        {/* Technician Columns */}
        {data.technicians.map((technician) => (
          <Box key={technician.id} sx={{ minWidth: '300px', maxWidth: '300px', height: '100%' }}>
            <TechnicianColumn
              technician={technician}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onCardClick={handleJobCardClick}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              draggedBooking={draggedBooking}
              onStartHealthCheck={handleStartHealthCheck}
              onEdit={handleEditBooking}
              isTechnician={isTechnician}
            />
          </Box>
        ))}

        {data.technicians.length === 0 && (
          <Paper
            sx={{
              minWidth: '300px',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
            }}
          >
            <Typography variant="body2">
              No technicians available. Add technicians to get started.
            </Typography>
          </Paper>
        )}
      </Box>

      <Box mt={3}>
        <Typography variant="caption" color="text.secondary">
          💡 Tip: Drag and drop jobs between columns to assign/unassign them to technicians.
          Jobs will automatically update their status when assigned. Click on any job card to view details.
        </Typography>
      </Box>

      {/* Booking Detail Modal */}
      <Dialog
        open={detailModalOpen}
        onClose={handleCloseDetailModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              Booking Details - {selectedBooking?.customer_name}
            </Typography>
            <Chip
              label={selectedBooking?.status?.replace('_', ' ')}
              color={selectedBooking?.status === 'confirmed' ? 'primary' :
                     selectedBooking?.status === 'assigned' ? 'warning' :
                     selectedBooking?.status === 'in_progress' ? 'success' : 'default'}
              size="small"
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Customer Information
                </Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {selectedBooking.customer_name}
                </Typography>
                {selectedBooking.customer_phone && (
                  <Typography variant="body2">
                    <strong>Phone:</strong> {selectedBooking.customer_phone}
                  </Typography>
                )}

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Vehicle Information
                </Typography>
                <Typography variant="body2">
                  <strong>Vehicle:</strong> {selectedBooking.vehicle_info}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Booking Details
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong> {new Date(selectedBooking.booking_date).toLocaleDateString()}
                </Typography>
                {selectedBooking.time_of_day && (
                  <Typography variant="body2">
                    <strong>Time:</strong> {selectedBooking.time_of_day}
                  </Typography>
                )}
                {selectedBooking.location && (
                  <Typography variant="body2">
                    <strong>Location:</strong> {selectedBooking.location}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>Status:</strong> {selectedBooking.status.replace('_', ' ')}
                </Typography>
                {/* Hide total cost for technicians */}
                {!isTechnician && (
                  <Typography variant="body2">
                    <strong>Total Cost:</strong> £{selectedBooking.total_cost?.toFixed(2)}
                  </Typography>
                )}
                {selectedBooking.estimated_completion_time && (
                  <Typography variant="body2">
                    <strong>Estimated Time:</strong> {selectedBooking.estimated_completion_time}
                  </Typography>
                )}
              </Grid>

              {selectedBooking.work_required && selectedBooking.work_required.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Work Required
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {selectedBooking.work_required.map((work, index) => (
                      <Chip
                        key={index}
                        label={isTechnician
                          ? (typeof work === 'string' ? work : work.service || 'Service')
                          : `${typeof work === 'string' ? work : work.service || 'Service'} ${
                              work.cost ? `- £${work.cost}` : ''
                            }`
                        }
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </Grid>
              )}

              {(selectedBooking.notes || selectedBooking.technician_notes) && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Notes
                  </Typography>
                  {selectedBooking.notes && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>General Notes:</strong> {selectedBooking.notes}
                    </Typography>
                  )}
                  {selectedBooking.technician_notes && (
                    <Typography variant="body2">
                      <strong>Technician Notes:</strong> {selectedBooking.technician_notes}
                    </Typography>
                  )}
                </Grid>
              )}

              {selectedBooking.healthChecks && selectedBooking.healthChecks.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    📋 Vehicle Health Checks ({selectedBooking.healthChecks.length})
                  </Typography>
                  {selectedBooking.healthChecks.map((hc, index) => (
                    <Paper
                      key={hc.id}
                      sx={{
                        p: 2,
                        mb: 2,
                        backgroundColor: '#f8f9fa',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#e9ecef',
                          boxShadow: 2
                        }
                      }}
                      onClick={() => {
                        setHealthCheckBooking(selectedBooking);
                        setViewHealthCheckId(hc.id);
                        setDetailModalOpen(false);
                        setHealthCheckModalOpen(true);
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" fontWeight="bold">
                          {hc.document_number}
                        </Typography>
                        <Chip
                          label={hc.status}
                          size="small"
                          color={hc.status === 'completed' ? 'success' : 'warning'}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Technician: {hc.technician} | Created: {new Date(hc.created_at).toLocaleString()}
                      </Typography>
                      {hc.completed_at && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Completed: {new Date(hc.completed_at).toLocaleString()}
                        </Typography>
                      )}
                      <Box mt={1} display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Chip label={`🔴 ${hc.status_counts.red}`} size="small" sx={{ mr: 0.5 }} />
                          <Chip label={`🟡 ${hc.status_counts.amber}`} size="small" sx={{ mr: 0.5 }} />
                          <Chip label={`🟢 ${hc.status_counts.green}`} size="small" />
                        </Box>
                        <Typography variant="caption" color="primary" fontWeight="bold">
                          Click to view →
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Grid>
              )}

              {selectedBooking.job_request && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom sx={{ color: 'success.main', fontWeight: 'bold' }}>
                    Parts Request Completed ✓
                  </Typography>

                  {selectedBooking.job_request.technician_notes && (
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <strong>Technician Request:</strong> {selectedBooking.job_request.technician_notes}
                    </Typography>
                  )}

                  {selectedBooking.job_request.admin_notes && (
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <strong>Admin Notes:</strong> {selectedBooking.job_request.admin_notes}
                    </Typography>
                  )}

                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    {isTechnician ? 'Parts Required' : 'Parts & Pricing'}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {selectedBooking.job_request.items && [...selectedBooking.job_request.items]
                      .sort((a, b) => (b.is_paid ? 1 : 0) - (a.is_paid ? 1 : 0))
                      .map((item, index) => {
                      const priorityConfig = {
                        high: { color: '#f44336', symbol: '🔴' },
                        medium: { color: '#ff9800', symbol: '🟡' },
                        low: { color: '#4caf50', symbol: '🟢' }
                      };
                      const priority = priorityConfig[item.priority] || priorityConfig.medium;

                      return (
                        <Box key={index} sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 0.5,
                          borderBottom: index < selectedBooking.job_request.items.length - 1 ? '1px solid #eee' : 'none',
                          opacity: item.is_paid ? 1 : 0.6,
                          backgroundColor: item.is_paid ? '#e8f5e9' : 'transparent',
                          px: 1,
                          borderRadius: 1,
                        }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {item.is_paid && (
                              <Chip
                                label="✓"
                                size="small"
                                sx={{
                                  minWidth: 'auto',
                                  fontSize: '0.7rem',
                                  height: 20,
                                  backgroundColor: '#4caf50',
                                  color: 'white',
                                  fontWeight: 'bold',
                                }}
                              />
                            )}
                            <Chip
                              label={priority.symbol}
                              size="small"
                              sx={{
                                minWidth: 'auto',
                                fontSize: '0.7rem',
                                height: 20,
                                backgroundColor: `${priority.color}20`,
                                color: priority.color,
                                border: `1px solid ${priority.color}40`
                              }}
                            />
                            <Box>
                              <Typography variant="body2">
                                <strong>{item.quantity}x</strong> {item.name}
                                {!item.is_paid && (
                                  <Chip
                                    label="NOT PAID"
                                    size="small"
                                    sx={{
                                      ml: 1,
                                      fontSize: '0.6rem',
                                      height: 16,
                                      backgroundColor: '#ffebee',
                                      color: '#c62828',
                                      fontWeight: 'bold',
                                    }}
                                  />
                                )}
                              </Typography>
                              {item.part_number && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Part #: {item.part_number}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          {/* Hide price for technicians */}
                          {!isTechnician && (
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              £{(item.price * item.quantity).toFixed(2)}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Hide cost totals for technicians */}
                  {!isTechnician && (
                    <>
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pt: 1,
                        borderTop: '2px solid #4caf50'
                      }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          Additional Parts Cost:
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          £{selectedBooking.job_request.total_additional_cost?.toFixed(2)}
                        </Typography>
                      </Box>

                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pt: 1,
                        mt: 1,
                        borderTop: '2px solid #2196f3'
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          Total Job Cost:
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          £{(selectedBooking.total_cost + (selectedBooking.job_request.total_additional_cost || 0)).toFixed(2)}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Grid>
              )}

              {selectedBooking.actual_start_time && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Progress Tracking
                  </Typography>
                  <Typography variant="body2">
                    <strong>Started:</strong> {new Date(selectedBooking.actual_start_time).toLocaleString()}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailModal}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Parts Request modal removed */}

      {/* Vehicle Health Check Modal */}
      <Dialog
        open={healthCheckModalOpen}
        onClose={handleCloseHealthCheckModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Vehicle Health Check</Typography>
            <IconButton onClick={handleCloseHealthCheckModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {healthCheckBooking && (
            <VehicleHealthCheck
              booking={healthCheckBooking}
              healthCheckId={viewHealthCheckId}
              onClose={handleCloseHealthCheckModal}
              onComplete={handleHealthCheckComplete}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Booking Modal */}
      <EditBookingModal
        open={editModalOpen}
        onClose={handleEditModalClose}
        booking={editingBooking}
        onSave={handleEditSave}
      />
    </Box>
  );
};

export default JobBoard;